// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation, getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import StyledBox, { StyledBoxStyles } from "@keller/App/Components/Common/StyledBox";
import parse from "html-react-parser";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    text = "text",
    fullWidthButton = "fullWidthButton",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.text]: string;
        [fields.fullWidthButton]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    let { search } = getLocation(state);
    const { allowCreateAccount, allowGuestCheckout } = getSettingsCollection(state).accountSettings;
    let returnUrl = getReturnUrl(state);
    if (!returnUrl && state.components.signInOverlay.isOpen) {
        returnUrl = getLocation(state).pathname;
        if (!search) {
            search = `?returnUrl=${returnUrl}`;
        }
    }

    const shippingPageUrl = getPageLinkByPageType(state, "CheckoutShippingPage")?.url;
    const referredFromShipping = returnUrl === shippingPageUrl;
    const onePageCheckoutPageUrl = getPageLinkByPageType(state, "OnePageCheckoutPage")?.url;
    const referredFromOnePageCheckout = returnUrl === onePageCheckoutPageUrl;
    const createAccountPageLink = getPageLinkByPageType(state, "CreateAccountPage");
    return {
        allowCreateAccount,
        allowGuestCheckout:
            allowGuestCheckout &&
            !getCurrentUserIsGuest(state) &&
            (referredFromShipping || referredFromOnePageCheckout),
        returnUrl,
        createAccountUrl: createAccountPageLink ? `${createAccountPageLink.url}${search}` : undefined,
    };
};

const mapDispatchToProps = {
    signInAsGuest,
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface SignInCreateNewAccountStyles {
    signInCreateNewAccountGridContainer?: GridContainerProps;
    signInCreateNewAccountTitleGridItem?: GridItemProps;
    signInCreateNewAccountTextGridItem?: GridItemProps;
    signInCreateNewAccountButtonGridItem?: GridItemProps;
    signInCreateNewAccountTitle?: TypographyProps;
    signInCreateNewAccountText?: TypographyProps;
    checkoutAsGuestButton?: ButtonPresentationProps;
    createNewAccountButton?: ButtonPresentationProps;
    // XNG-Changes
    styledBoxStyles?: StyledBoxStyles;
    signInCreateNewAccountInfo?: InjectableCss;
    signInCreateNewAccountActions?: InjectableCss;
}

export const signInCreateNewAccountStyles: SignInCreateNewAccountStyles = {
    styledBoxStyles: {
        container: {
            css: css`
                padding: 15px;
                height: 100%;
                display: flex;
                flex-direction: column;
                justify-content: space-between;
                max-width: 528px;
            `,
        },
    },
    signInCreateNewAccountTitle: {
        variant: "h4",
        size: 24,
        weight: 900,
        css: css`
            line-height: 30px;
        `,
    },
    checkoutAsGuestButton: {
        variant: "secondary",
        mergeCss: true,
        css: css`
            width: 100%;
            margin-bottom: 15px;
        `,
    },
    signInCreateNewAccountActions: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    createNewAccountButton: {
        variant: "primary",
        mergeCss: true,
        css: css`
            width: 100%;
        `,
    },
};

/**
 * @deprecated Use signInCreateNewAccountStyles instead.
 */
export const signInCreateNewAccount = signInCreateNewAccountStyles;
const styles = signInCreateNewAccountStyles;

const SignInCreateNewAccount: FC<Props> = ({
    history,
    fields,
    allowCreateAccount,
    allowGuestCheckout,
    returnUrl,
    createAccountUrl,
    signInAsGuest,
}) => {
    if (!allowCreateAccount) {
        return null;
    }

    const handleCheckoutAsGuest = () => {
        if (!returnUrl) {
            return;
        }

        signInAsGuest({ returnUrl });
    };

    const handleCreateNewAccount = () => {
        if (!createAccountUrl) {
            return;
        }

        history.push(createAccountUrl);
    };

    const createButtonCss = fields.fullWidthButton
        ? css`
              width: 100%;
          `
        : undefined;

    // XNG-Changes: Update structure
    return (
        <StyledBox extendedStyles={styles.styledBoxStyles}>
            <StyledWrapper {...styles.signInCreateNewAccountInfo}>
                <Typography {...styles.signInCreateNewAccountTitle}>{translate("Create New Account")}</Typography>
                <Typography {...styles.signInCreateNewAccountText}>{parse(fields.text, parserOptions)}</Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.signInCreateNewAccountActions}>
                {allowGuestCheckout && (
                    <Button
                        {...styles.checkoutAsGuestButton}
                        onClick={handleCheckoutAsGuest}
                        data-test-selector="signInCreateNewAccount_checkoutAsGuest"
                    >
                        {translate("Checkout as Guest")}
                    </Button>
                )}
                {createAccountUrl && (
                    <Button
                        {...styles.createNewAccountButton}
                        onClick={handleCreateNewAccount}
                        data-test-selector="signInCreateNewAccount_createNewAccount"
                    >
                        {translate("Create a New Account")}
                    </Button>
                )}
            </StyledWrapper>
        </StyledBox>
    );
};

// XNG-Changes: Update editorTemplate to RichTextField
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(SignInCreateNewAccount)),
    definition: {
        allowedContexts: ["SignInPage", "SignInOverlay"],
        group: "Sign In",
        icon: "User",
        fieldDefinitions: [
            {
                name: fields.text,
                displayName: "Description",
                editorTemplate: "RichTextField",
                defaultValue: "Create an account to checkout faster, view order history, save product lists, and more!",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            // {
            //     name: fields.fullWidthButton,
            //     displayName: "Full Width Button",
            //     editorTemplate: "CheckboxField",
            //     defaultValue: false,
            //     fieldType: "General",
            //     sortOrder: 1,
            // },
        ],
    },
};

export default widgetModule;
